import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TerraMatrixBrand } from "@/components/branding/TerraMatrixBrand";
import { useResponsiveInfo } from "@/hooks/useResponsiveViewport";
import { SignInModal } from "@/components/auth/SignInModal";


export interface PublicNavigationProps {
  scrolled: boolean;
  onContactClick: () => void;
}

export const PublicNavigation: React.FC<PublicNavigationProps> = ({
  scrolled,
  onContactClick,
}) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [signInModalOpen, setSignInModalOpen] = useState(false);
  const { isMobile } = useResponsiveInfo();

  const handleSignInClick = () => {
    if (isMobile) {
      navigate("/login");
    } else {
      setSignInModalOpen(true);
    }
    setMobileMenuOpen(false);
  };

  const handleContactClick = () => {
    setMobileMenuOpen(false);
    onContactClick();
  };

  return (
    <>
      <nav
        className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "top-4 px-4 sm:px-6 lg:px-8" : "top-0"
        }`}
      >
        <div
          className={`mx-auto transition-all duration-300 ${
            scrolled
              ? "max-w-6xl backdrop-blur-md shadow-lg rounded-2xl"
              : "max-w-7xl"
          }`}
          style={{
            backgroundColor: scrolled ? "hsl(var(--card) / 0.9)" : "transparent",
          }}
        >
          <div className={`px-4 sm:px-6 lg:px-8 ${scrolled ? "px-6" : ""}`}>
            <div className="flex items-center justify-between h-[60px]">
              {/* Logo - Left */}
              <div className="flex-1 flex items-center">
                <a href="/" className="flex-shrink-0">
                  <TerraMatrixBrand
                    markClassName="h-8 w-8 md:h-10 md:w-10"
                    nameClassName="text-xl md:text-2xl text-white"
                    className="transition-all duration-300"
                  />
                </a>
              </div>

              {/* Desktop Navigation - Center */}
              <div className="hidden md:flex items-center justify-center gap-6">
                <Button
                  variant="ghost"
                  className="font-medium transition-colors duration-200 hover:bg-white/20 text-white"
                  onClick={() => navigate("/")}
                >
                  Home
                </Button>
                <Button
                  variant="ghost"
                  className="font-medium transition-colors duration-200 hover:bg-white/20 text-white"
                  onClick={() => navigate("/about")}
                >
                  About
                </Button>
                <Button
                  variant="ghost"
                  className="font-medium transition-colors duration-200 hover:bg-white/20 text-white"
                  onClick={handleContactClick}
                >
                  Contact
                </Button>
              </div>

              {/* Desktop Sign In - Right */}
              <div className="flex-1 hidden md:flex items-center justify-end">
                <Button
                  variant="ghost"
                  className="font-medium rounded transition-all duration-200 bg-transparent text-white hover:bg-white/20 hover:text-white"
                  onClick={handleSignInClick}
                >
                  Sign In
                </Button>
              </div>

              {/* Mobile menu button */}
              <button
                className="md:hidden p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="text-white" size={24} />
                ) : (
                  <Menu className="text-white" size={24} />
                )}
              </button>
            </div>

            {/* Mobile menu */}
            {mobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="md:hidden bg-card border border-border rounded-lg shadow-lg p-4 mb-4"
              >
                <div className="flex flex-col gap-3">
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-foreground transition-colors duration-200"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/");
                    }}
                  >
                    Home
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-foreground transition-colors duration-200"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/about");
                    }}
                  >
                    About
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-foreground transition-colors duration-200"
                    onClick={handleContactClick}
                  >
                    Contact
                  </Button>
                  <hr className="border-border" />
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-foreground transition-colors duration-200"
                    onClick={handleSignInClick}
                  >
                    Sign In
                  </Button>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </nav>

      {/* Sign In Modal */}
      <SignInModal
        isOpen={signInModalOpen}
        onClose={() => setSignInModalOpen(false)}
      />
    </>
  );
};
